import useControlState from '@util/hooks/use-control-state/use-control-state.hook';
import { memo, PropsWithChildren, Suspense, SuspenseProps, useEffect } from 'react';
import LoadingIndicator from '../loading-indicator/loading-indicator.molecule';

// #region Types
type SuspenseLoadingFallbackProps = PropsWithChildren<{
  onAttachChange: (isSuspense: boolean) => void;
}>;

export type SuspenseLoadingProps = SuspenseProps & {
  onIsSuspenseChange?: (isSuspense: boolean) => void;
  withLoadingIndicator?: boolean;
};
// #endregion

// #region Sub Components
function BaseSuspenseLoadingFallback({ children, onAttachChange }: SuspenseLoadingFallbackProps) {
  // #region Effects
  useEffect(() => {
    onAttachChange(true);

    return () => {
      onAttachChange(false);
    };
  }, [onAttachChange]);
  // #endregion

  return children;
}
BaseSuspenseLoadingFallback.displayName = 'SuspenseLoadingFallback';
const SuspenseLoadingFallback = memo(BaseSuspenseLoadingFallback);
// #endregion

function BaseSuspenseLoading({
  children,
  fallback,
  onIsSuspenseChange,
  withLoadingIndicator = true,
  ...rest
}: SuspenseLoadingProps) {
  // #region States
  const [isSuspense, setIsSuspense] = useControlState({
    controller: onIsSuspenseChange,
    initial: false,
  });
  // #endregion

  return (
    <>
      <Suspense
        {...rest}
        fallback={
          <SuspenseLoadingFallback onAttachChange={setIsSuspense}>
            {fallback}
          </SuspenseLoadingFallback>
        }
      >
        {children}
      </Suspense>

      {withLoadingIndicator && <LoadingIndicator open={isSuspense} />}
    </>
  );
}
BaseSuspenseLoading.displayName = 'SuspenseLoading';

const SuspenseLoading = memo(BaseSuspenseLoading);

export default SuspenseLoading;
